import { isDisplayable }        from '../../../shared/utils/productUtils.js'
import { AVAILABILITY_STATUS }  from '../../../shared/constants/availabilityStatus.js'

/**
 * Derives the product-relevant Inventory tier collection.
 *
 * The derivation collects occupied `(building_id, ref_tier)` pairs from product
 * tier data and determines the highest occupied tier in each building. It then
 * keeps every source tier up to that boundary, preserving lower unoccupied
 * levels while excluding technical levels above all products. Buildings with
 * no products are omitted.
 *
 * A tier is marked as sold out when it contains products but none of them is
 * both displayable and available/reserved. Matching CMS tier settings are
 * joined by their composite tier identifier and provide activation and display
 * name overrides. Input arrays and their entries are not mutated.
 *
 * @param {Object[]} buildings - Structurally normalized buildings loaded from `buildings.json`.
 * @param {Object[]} products - Normalized products containing building and tier relations.
 * @param {Object[]} [tiersSettings=[]] - CMS `tiers_settings.tiers` entries.
 * @returns {Object[]} Derived tiers enriched with identifiers, CMS settings, activation state, and sold-out state.
 */
export function buildTiersData(buildings, products, tiersSettings = []) {
  if (!buildings?.length || !products?.length) return []

  // Step 1: collect (building_id, ref_tier) pairs that have at least one product tier
  const occupiedKeys = new Set()
  const availableKeys = new Set()

  for (const product of products) {
    if (!product.tiers?.length) continue

    const buildingId = product.buildingId
      ?? (typeof product.id_cad === 'string' ? product.id_cad.split('-')[0] : null)
    if (!buildingId) continue

    const isAvailableOrReserved = 
          isDisplayable(product.visibility) && 
          product.status !== AVAILABILITY_STATUS.SOLD && 
          product.status !== AVAILABILITY_STATUS.HOLD

    for (const tier of product.tiers) {
      if (tier.ref_tier != null) {
        const key = `${buildingId}||${tier.ref_tier}`

        occupiedKeys.add(key)
        
        if (isAvailableOrReserved) {
          availableKeys.add(key)
        }      }
    }
  }

  // Step 2: per building, find the maximum occupied ref_tier
  const buildingMaxTier = new Map()
  for (const building of buildings) {
    let max = -Infinity
    for (const tier of (building.tiers ?? [])) {
      if (occupiedKeys.has(`${building.id_cad}||${tier.ref_tier}`)) {
        if (tier.ref_tier > max) max = tier.ref_tier
      }
    }
    if (max !== -Infinity) {
      buildingMaxTier.set(building.id_cad, max)
    }
  }

  // Step 3: keep all building tiers <= max occupied tier
  const occupiedTiers = []
  for (const building of buildings) {
    const max = buildingMaxTier.get(building.id_cad)
    if (max === undefined) continue // no products for this building → skip
    
    for (const tier of (building.tiers ?? [])) {
      if (tier.ref_tier <= max) {
        const tierKey = `${building.id_cad}||${tier.ref_tier}`
        const isSoldOut = occupiedKeys.has(tierKey) && !availableKeys.has(tierKey)

        occupiedTiers.push({
          ref_tier:       tier.ref_tier,
          name_original:  tier.name_original,
          id:             `${building.id_cad}-${tier.id_cad}`, //use this or remove? id is used in panelSettings
          id_cad:         `${building.id_cad}-${tier.id_cad}`, //maybe id_cad should only be for apartment?
          id_cad_tier:    tier.id_cad, //when to char third part of id_cad
          building_id:    building.id_cad,
          isSoldOut,
        })
      }
    }
  }

  // Step 4: Join tier objects with panelSettings
  // TOOD: decide - remove not active tiers from results or later use `is_active` condition?
  // TODO: change keys to camelCase to separate them from JSON keys
  const settingsMap = new Map();

  for ( const setting of tiersSettings ) {
    if (setting.id) {
      settingsMap.set(setting.id, {
        is_active: setting.is_active,
        cms_tier_name: setting.cms_tier_name,
        cms_tier_name_short: setting.cms_tier_name_short,
      })
    }
  }

  const result = [];

  for ( const tier of occupiedTiers ) {
    const compositeKeyFallback = `${tier.building_id}-${tier.id_cad_tier}`
    
    const matchedSetting =
      (tier.id && settingsMap.get(tier.id)) ||
      (tier.id_cad && settingsMap.get(tier.id_cad)) ||
      settingsMap.get(compositeKeyFallback) ||
      {}

    result.push({
      ...tier,
      isActive: matchedSetting?.is_active ?? true,
      is_active: matchedSetting?.is_active ?? true,
      cms_tier_name: matchedSetting?.cms_tier_name || "",
      cms_tier_name_short: matchedSetting?.cms_tier_name_short || "",
    })
  }

  return result
}
